import React, { useEffect, useState } from "react";
import axios from "axios";

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import { ModalDelete } from "../../components/ModalDelete";
import SelectCompany from "../../components/selects/SelectCompany";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { resolveImageUrl } from "../../helpers/brand";
import { canCreateBrands, canDeleteBrands } from "../../services/auth";

const statusFilterOptions = [
    { label: 'Todos os status', value: null },
    { label: 'Ativo', value: 1 },
    { label: 'Inativo', value: 0 }
];

export const BrandList = () => {
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState('');

    const [brands, setBrands] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [brandToDelete, setBrandToDelete] = useState(null);

    // Busca e filtros
    const [search, setSearch] = useState('');
    const [showFilter, setShowFilter] = useState(false);
    const [filterCompanyId, setFilterCompanyId] = useState(null);
    const [filterDomain, setFilterDomain] = useState('');
    const [filterActive, setFilterActive] = useState(null);

    const [shouldFetch, setShouldFetch] = useState(false);

    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        } else {
            setShouldFetch(true);
        }
    }, [filters]);

    useEffect(() => {
        getBrands();
    }, [currentPage]);

    useEffect(() => {
        if (shouldFetch) {
            getBrands();
            setShouldFetch(false);
        }
    }, [shouldFetch]);

    useEffect(() => {
        generateData();
    }, [brands]);

    const getBrands = () => {
        setBrands([]);
        setLoading(true);

        if (typeof window.brandCancelToken !== typeof undefined) {
            window.brandCancelToken?.cancel();
        }
        window.brandCancelToken = axios.CancelToken.source();

        api.get(`/brand/paginate?${filters}&page=${currentPage}`, {
            cancelToken: window.brandCancelToken.token
        }).then(res => {
            const pagination = res.data.resources.pagination;
            setBrands(res.data.resources.data);
            setCurrentPage(pagination.current_page);
            setLastPage(pagination.last_page);
            setPerPage(pagination.per_page);
            setTotalResults(pagination.total);
            setHasNextPage(pagination.next_page_url !== null);
            setHasPrevPage(pagination.prev_page_url !== null);
        }).catch(() => {
        }).then(() => setLoading(false));
    };

    const generateData = () => {
        const header = ['Favicon', 'Nome da marca', 'Url do site', 'Empresa vinculada', 'Qtd. de pessoas', 'Status', 'Ações'];
        const rows = [];

        brands.forEach(brand => {
            const faviconSrc = resolveImageUrl(brand.favicon_url);
            const logo = faviconSrc
                ? <img className="brand-logo" src={faviconSrc} alt={brand.name} />
                : <img className="brand-logo" src={require(`../../assets/images/svgs/default.svg`).default} alt={brand.name} />;

            const url = brand.domain
                ? <a href={`https://${brand.domain}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>{brand.domain}</a>
                : '—';

            const status = (
                <span className={`brand-status-badge ${brand.active ? 'ok' : 'pending'}`}>
                    {brand.active ? 'Ativo' : 'Inativo'}
                </span>
            );

            rows.push({
                data: [
                    logo,
                    brand.name,
                    url,
                    brand.company?.trade_name ?? '—',
                    brand.customers_count ?? brand.companies_count ?? 0,
                    status
                ],
                buttons: canDeleteBrands()
                    ? [<Button key={brand.id} type={`delete`} svg={`delete`} action={() => setBrandToDelete(brand.id)} />]
                    : [],
                to: `/brand/edit/${brand.id}`
            });
        });

        setData({ header, rows });
    };

    // Compõe a string de filtros (busca + filtros avançados) e dispara a listagem
    const applyFilters = () => {
        const parts = [];
        let i = 0;

        if (search) parts.push(`filters[${i++}][name][contain]=${search}`);
        if (filterDomain) parts.push(`filters[${i++}][domain][contain]=${filterDomain}`);
        if (filterCompanyId) parts.push(`filters[${i++}][company_id][equal]=${filterCompanyId}`);
        if (filterActive !== null) parts.push(`filters[${i++}][active][equal]=${filterActive}`);

        setCurrentPage(1);
        setFilters(parts.join('&'));
    };

    const handleApplyAdvancedFilters = () => {
        applyFilters();
        setShowFilter(false);
    };

    const clearAdvancedFilters = () => {
        setSearch('');
        setFilterCompanyId(null);
        setFilterDomain('');
        setFilterActive(null);
    };

    const deleteBrand = () => {
        setLoading(true);

        api.delete(`/brand/${brandToDelete}`).then(() => {
            setBrandToDelete(null);
            getBrands();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    };

    return (
        <>
            <ModalDelete
                show={brandToDelete !== null}
                action={deleteBrand}
                loading={loading}
                close={() => setBrandToDelete(null)}
            />
            <Modal
                title={`Filtrar marcas`}
                show={showFilter}
                close={() => setShowFilter(false)}
                footer={
                    <>
                        <Button type={`secondary`} size={`small`} text={`Limpar`} action={clearAdvancedFilters} />
                        <Button type={`primary`} size={`small`} text={`Filtrar`} svg={`filter-white`} action={handleApplyAdvancedFilters} />
                    </>
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Nome da marca`} change={setSearch} value={search} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Status`} options={statusFilterOptions} value={filterActive} change={setFilterActive} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <SelectCompany
                                label={`Empresa vinculada`}
                                selectedCompany={filterCompanyId}
                                setSelectedCompany={setFilterCompanyId}
                                fields={['trade_name']}
                                minSearch={3}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <Input type={`text`} label={`Domínio`} change={setFilterDomain} value={filterDomain} />
                        </div>
                    </div>
                </div>
            </Modal>
            <PanelTemplate id="brand-list">
                <PageHeader
                    left={
                        <div className="page-title">
                            <h1>Marcas</h1>
                            <p>Gerencie as marcas cadastradas no sistema.</p>
                        </div>
                    }
                    right={
                        <div className="brand-actions">
                            <div className="brand-search">
                                <input
                                    type="text"
                                    placeholder="Buscar marca..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && applyFilters()}
                                />
                                <button onClick={applyFilters}>
                                    <img src={require(`../../assets/images/svgs/search.svg`).default} alt="Buscar" />
                                </button>
                            </div>
                            <Button type={`secondary`} size={`small`} text={`Filtros`} svg="filter" action={() => setShowFilter(true)} />
                            {canCreateBrands() &&
                                <Button type={`primary`} size={`small`} text={`Nova marca`} svg="plus-white" link={`/brand/create`} />
                            }
                        </div>
                    }
                />
                <Box className={`no-padding with-pagination`}>
                    <Table
                        header={data.header}
                        rows={data.rows}
                        loading={loading}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={setCurrentPage}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default BrandList;
